"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ClipboardCheck, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Compatibility() {
  const shouldReduceMotion = useReducedMotion();
  const buyerChecklist = ["Machine manufacturer/brand", "Model name/number", "Serial number", "Approximate year", "Existing part number", "Clear photos of existing component", "Photos of connectors/plugs/mounting points", "Controller or control-box label", "Relevant dimensions", "Description of part being replaced"];
  return <section id="whats-included-compatibility" data-nav="light" className="border-t border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 border-b border-border pb-8 md:mb-16">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-3xl">
              <span className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                Compatibility Guide
              </span>
              <h2 className="mt-2 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl md:text-5xl">
                Confirm Compatibility Before Ordering a Mechanical Bull Part
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                The most important question is not simply whether a replacement
                part looks right. It is whether it actually matches the
                mechanical bull you own. Mechanical bulls can use different
                controllers, wiring arrangements, motors, connectors,
                inflatable dimensions and component generations. As a result, a
                part that fits one machine may not fit another model from the
                same general category. Before enquiring about a replacement
                part, identify as much information as you can about your
                existing equipment. That gives us a much better starting point
                than relying on appearance alone.
              </p>
            </div>
            <div className="mt-4 shrink-0 md:mt-0">
              <Button asChild variant="outline" className="h-11 rounded-none border-foreground px-5 font-serif text-sm font-semibold tracking-wider uppercase text-foreground hover:bg-foreground hover:text-background">
                <Link to="/ContactPage">
                  Ask About Part Compatibility
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Full-width Buyer Checklist Grid */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col">
          <div className="mb-6 flex items-center gap-3 border-l-2 border-primary pl-4">
            <ClipboardCheck className="size-5 text-primary" />
            <div>
              <h3 className="font-serif text-2xl font-bold tracking-tight uppercase">
                Information to Gather Before Enquiring
              </h3>
              <p className="text-sm text-muted-foreground">
                Document as many of these details as possible to verify fit before dispatch.
              </p>
            </div>
          </div>

          <Card className="rounded-none border border-border bg-card shadow-none">
            <CardHeader className="border-b border-border bg-muted/40 p-5">
              <CardTitle className="font-mono text-xs font-medium tracking-wider uppercase text-muted-foreground">
                Buyer Verification Checklist
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-y-0 sm:divide-x">
                <div className="divide-y divide-border">
                  {buyerChecklist.slice(0, 5).map((item, i) => <div key={item} data-index={i} className="flex items-center gap-3.5 p-5 transition-[background-color] duration-150 hover:bg-muted/30">
                      <CheckCircle2 className="size-4 shrink-0 text-primary" />
                      <span className="font-serif text-base font-semibold tracking-wide text-foreground sm:text-lg">
                        {item}
                      </span>
                    </div>)}
                </div>
                <div className="divide-y divide-border border-t border-border sm:border-t-0">
                  {buyerChecklist.slice(5).map((item, i) => <div key={item} data-index={i + 5} className="flex items-center gap-3.5 p-5 transition-[background-color] duration-150 hover:bg-muted/30">
                      <CheckCircle2 className="size-4 shrink-0 text-primary" />
                      <span className="font-serif text-base font-semibold tracking-wide text-foreground sm:text-lg">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Explicit Closing Note */}
          <div className="mt-4 border border-border bg-muted/20 p-4 font-sans text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Note: </span>
            If you do not know some of these details, send the information you do
            have. Do not guess at a model number or electrical specification
            simply to complete the enquiry.
          </div>
        </motion.div>

        {/* Technical Callout Bar */}
        <div className="mt-12 border border-border bg-muted/30 p-6 sm:p-8">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <div className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                Direct Verification Assistance
              </div>
              <h4 className="mt-1 font-serif text-xl font-bold uppercase sm:text-2xl">
                Need Help Identifying Your Mechanical Bull Setup?
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                If you do not know some of these details, send the information you do have. Do not guess at a model number or electrical specification simply to complete the enquiry.
              </p>
            </div>
            <div className="shrink-0">
              <Button asChild className="h-11 rounded-none bg-primary px-6 font-serif text-sm font-semibold tracking-wider uppercase text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  ASK ABOUT PART COMPATIBILITY
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}